import Column from '@/components/Schedules/Kanban/Column';
import { DragDropContext, OnDragEndResponder } from 'react-beautiful-dnd';
import './style.less';
import { SchedulesKanbanQuery } from '@/core/domain/schedules/NewSchedules/NewSchedules.types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useActivityList,
  useActivityListPaginate,
  useActivityStatusChange,
} from '@/core/domain/visual-management/activities/activities.hooks';
import { usePendingActivityList } from '@/core/domain/visual-management/pending-activities/pending-activities.hooks';
import { GPActivityColumn } from '@/core/domain/visual-management/activities/activities.types';
import { message } from 'antd';

interface Props {
  queryParams: SchedulesKanbanQuery;
}

const ScheduleDragDropContext: React.FC<Props> = ({ queryParams }) => {
  const activityList = useActivityList({
    // estimatorId: store.employeeId,
    paginateBy: 2,
    order: 'DESC',
    companyIds: queryParams.companyIds,
    limit: 10,
  });

  const activityListPaginate = useActivityListPaginate({
    // estimatorId: store.employeeId,
    paginateBy: 2,
    order: 'DESC',
    companyIds: queryParams.companyIds,
    limit: 10,
  });

  const pendingActivityList = usePendingActivityList({
    // estimatorId: store.employeeId,
    limit: 10,
    companyIds: queryParams.companyIds,
  });

  const activityStatusChange = useActivityStatusChange();

  const pendingActivities =
    pendingActivityList.data?.pages.flatMap((page) => page.activities) || [];
  const columns = activityList.data?.columns || [];

  const allColumns: GPActivityColumn[] = useMemo(
    () => [
      {
        key: 'pending',
        statusColor: 'gray',
        statusName: 'Pendentes',
        activities: pendingActivities,
        count: pendingActivities.length,
      },
      ...columns.map((col) => ({ ...col, key: col.statusId as string })),
    ],
    [columns, pendingActivities],
  );

  const [currentColumns, setCurrentColumns] =
    useState<GPActivityColumn[]>(allColumns);

  useEffect(() => {
    setCurrentColumns(allColumns);
  }, [activityList.dataUpdatedAt, pendingActivityList.dataUpdatedAt]);

  const handleDragEnd: OnDragEndResponder = async ({
    source,
    destination,
    draggableId,
  }) => {
    if (!destination || source.droppableId === destination.droppableId) return;

    if (destination.droppableId === 'pending') {
      message.warn('Não é possível mover para pendente');
      return;
    }

    const updatedColumns = [...currentColumns];
    const sourceColIndex = updatedColumns.findIndex(
      (col) => col.key === source.droppableId,
    );
    const destColIndex = updatedColumns.findIndex(
      (col) => col.key === destination.droppableId,
    );

    if (sourceColIndex === -1 || destColIndex === -1) return;

    const [movedActivity] = updatedColumns[sourceColIndex].activities.splice(
      source.index,
      1,
    );
    updatedColumns[destColIndex].activities.splice(
      destination.index,
      0,
      movedActivity,
    );
    updatedColumns[sourceColIndex].count -= 1;
    updatedColumns[destColIndex].count += 1;

    setCurrentColumns(updatedColumns);

    try {
      await activityStatusChange.mutateAsync({
        activityId: draggableId,
        statusId: destination.droppableId,
      });
    } catch {
      updatedColumns[sourceColIndex].activities.splice(
        source.index,
        0,
        movedActivity,
      );
      updatedColumns[destColIndex].activities.splice(destination.index, 1);
      updatedColumns[sourceColIndex].count += 1;
      updatedColumns[destColIndex].count -= 1;
      setCurrentColumns(updatedColumns);
    }
  };

  const onScrollEnd = useCallback(
    async (column: GPActivityColumn) => {
      if (activityListPaginate.isLoading || pendingActivityList.isFetching)
        return;

      if (column.key === 'pending') {
        if (pendingActivityList.hasNextPage) {
          await pendingActivityList.fetchNextPage();
        }
        return;
      }

      if (!column.nextToken) return;

      const response = await activityListPaginate.mutateAsync({
        statusId: column.statusId,
        nextToken: column.nextToken,
      });

      const resColumn = response.columns.find(
        (res) => res.statusId === column.key,
      );

      setCurrentColumns((prev) =>
        prev.map((prevColumn) => {
          if (prevColumn.key === column.key) {
            return {
              ...prevColumn,
              ...resColumn,
              activities: [
                ...prevColumn.activities,
                ...(resColumn?.activities || []),
              ],
            };
          }

          return prevColumn;
        }),
      );
    },
    [activityListPaginate, pendingActivityList],
  );

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="kanban-root">
        {currentColumns.map((column) => (
          <Column
            key={column.key}
            column={{
              color: column.statusColor,
              id: column.key,
              name: column.statusName,
            }}
            tasks={column.activities}
            onScrollEnd={() => onScrollEnd(column)}
          />
        ))}
      </div>
    </DragDropContext>
  );
};

export default ScheduleDragDropContext;
